import { CircularProgress, Grid } from "@mui/material";
import NavDetails from "components/Elements/NavDetails";
import { useCallback, useEffect, useState } from "react";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import RestoreIcon from "@mui/icons-material/Restore";
import { appPath, feedbackPath } from "helpers/routePaths";
import Notes from "./Notes";
import Documents from "./Documents";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import DefectImages from "./Images";
import { getLocalStorageData } from "helpers/utils";
import ChangeStatusPopup from "./ChangeStatusPopup";
import ColourConstants from "helpers/colourConstants";
import Details from "./details";
import AccessWrapper from "components/Modules/AccessWrapper";
import { getFeedbackDetails } from "services/feedback/feedbackdetails";
import API from "helpers/api";
import TabTitle from "components/Elements/TabTitle";
import { HistoryCaptions, NoReadOnly } from "helpers/constants";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";
import { makeStyles } from "tss-react/mui";
import HistoryBar from "components/Modules/HistorySidebar/HistoryBar";
import { useDispatch, useSelector } from "react-redux";
import { feedBackPage } from "services/History/models";
import {
	loginWithSiteAppId,
	setHistoryDrawerState,
	setLoggedInFromFeedbackUrl,
	showError,
} from "../../../redux/common/actions";

const AT = ActionButtonStyle();
const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	restore: {
		border: "2px solid",
		borderRadius: "100%",
		height: "35px",
		width: "35px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#307ad6",
	},

	buttons: {
		display: "flex",
		marginLeft: "auto",
		[media]: {
			marginLeft: "0px",
			flexDirection: "column",
			marginBottom: "10px",
			gap: "10px",
		},
	},
	wrapper: {
		display: "flex",
		[media]: {
			marginTop: "10px",
			justifyContent: "space-between",
			flexDirection: "column",
		},
	},
}));

function FeedbackDetails() {
	const { classes, cx } = useStyles();
	const { id } = useParams();
	const navigate = useNavigate();
	const { customCaptions, siteAppID, siteID, application } =
		getLocalStorageData("me");
	//to  check url
	const { search } = useLocation();
	const dispatch = useDispatch();
	const query = new URLSearchParams(search);
	const siteAppIdFromUrl = parseInt(query.get("siteAppId"));
	const isSameSiteAppId = siteAppIdFromUrl === siteAppID;

	const [showChangeStatus, setShowChangeStatus] = useState(false);
	const [details, setDetails] = useState({});
	const [loading, setLoading] = useState(true);

	const sessionMe = sessionStorage.getItem("me")
		? JSON.parse(sessionStorage.getItem("me"))
		: null;
	const localMe = localStorage.getItem("me")
		? JSON.parse(localStorage.getItem("me"))
		: null;
	const {
		position,
		isSiteUser,
		isAdmin,
		multiSiteUser,
		id: currentUserId,
	} = sessionMe || localMe || {};

	const handleToggleChangeStatus = () => setShowChangeStatus((prev) => !prev);

	const fetchDefect = useCallback(async () => {
		const response = await getFeedbackDetails(id);
		if (response.status) setDetails(response.data);
		setLoading(false);
		if (!response.status) {
			dispatch(showError(response?.data?.title));
		}
	}, [id]);

	const readOnly = position?.feedbackAccess === READONLY_ACCESS;
	const importButton = { backgroundColor: "#ED8738" };

	const { isHistoryDrawerOpen } = useSelector((state) => state.commonData);

	useEffect(() => {
		if (!siteAppIdFromUrl || (siteAppIdFromUrl && isSameSiteAppId)) {
			fetchDefect();
			return;
		}

		// login to site app
		if (siteAppIdFromUrl && !isSameSiteAppId) {
			const handleLogin = async () => {
				setLoading(true);
				dispatch(
					setLoggedInFromFeedbackUrl({ isLoggedInFromFeedbackUrl: true })
				);
				const response = await dispatch(
					loginWithSiteAppId(siteAppIdFromUrl, false)
				);
				setLoading(false);
				if (!response.status) {
					dispatch(showError(response?.data?.title ?? response?.data));
					if (multiSiteUser || isAdmin) {
						navigate("/app/portal");
						return;
					}
					if (isSiteUser) {
						navigate("/app/feedback");
						return;
					}
				}
			};

			handleLogin();
		}
	}, [id, siteAppIdFromUrl, isSameSiteAppId]);

	useEffect(() => {
		if (!id) {
			return;
		}

		const acknowledgeFeedback = async () => {
			try {
				await API.post(`/api/feedback/${id}/mark-read`);
			} catch {
				// Intentionally mute errors for mark-read
			}
		};

		acknowledgeFeedback();
	}, [id]);

	if (loading) return <CircularProgress />;

	return (
		<div className="container">
			<TabTitle
				title={`${customCaptions.feedback} ${details.number} | ${application.name}`}
			/>
			<ChangeStatusPopup
				open={showChangeStatus}
				onClose={handleToggleChangeStatus}
				setDetails={setDetails}
			/>
			<HistoryBar
				id={id}
				showhistorybar={isHistoryDrawerOpen}
				dispatch={dispatch}
				fetchdata={(id, pageNumber, pageSize) =>
					feedBackPage(id, pageNumber, pageSize)
				}
				origin={HistoryCaptions.feedback}
			/>
			<div className={"topContainerCustomCaptions"}>
				<NavDetails
					status={true}
					lastSaved={null}
					staticCrumbs={[
						{
							id: 1,
							name: customCaptions?.feedback || "Feedback",
							url: appPath + feedbackPath,
						},
						{
							id: 2,
							name: details.number,
						},
					]}
					hideLastLogin
					state={{
						modelStatusName: details?.statusName,
						statusColor:
							details.statusType === "C"
								? ColourConstants.green
								: ColourConstants.red,
					}}
					hideVersion={true}
					showAcknowledgedByAt={Boolean(details?.lastReadAt)}
					acknowledgedTime={details?.lastReadAt}
					acknowledgedUserName={details?.lastReadByUserName}
					acknowledgedLabel="Last Acknowledge By:"
					showCreatedByAt
					time={details.createdDateTime}
					userName={details.createdUserName}
				/>
				<div className={classes.wrapper}>
					<AccessWrapper
						access={position?.feedbackAccess}
						accessList={NoReadOnly}
					>
						<div className={classes.buttons}>
							<AT.GeneralButton
								style={importButton}
								onClick={handleToggleChangeStatus}
							>
								Change Status
							</AT.GeneralButton>
						</div>
					</AccessWrapper>

					<div
						className="restore"
						onClick={() => dispatch(setHistoryDrawerState(true))}
					>
						<RestoreIcon className={classes.restore} />
					</div>
				</div>
			</div>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Details
						details={details}
						siteAppID={siteAppID}
						siteId={siteID}
						captions={customCaptions}
						feedbackId={id}
						isReadOnly={readOnly}
					/>
				</Grid>
				<Grid item xs={12} lg={6}>
					<DefectImages
						feedbackId={id}
						captions={customCaptions}
						isReadOnly={readOnly}
					/>
				</Grid>
				<Grid item xs={12} lg={6}>
					<Documents
						feedbackId={id}
						isReadOnly={readOnly}
						customCaptions={customCaptions}
					/>
					<Notes feedbackId={id} isReadOnly={readOnly} />
				</Grid>
			</Grid>
		</div>
	);
}

export default FeedbackDetails;
